import React, { useState, useEffect, useMemo } from "react";
import {
    Search,
    Users,
    UserPlus,
    Eye,
    Star,
    X
} from "lucide-react";
import axios from 'axios';
import Swal from 'sweetalert2';
import withReactContent from "sweetalert2-react-content";
import { base_api_url } from '../../../environment.jsx';
import "./client.css";

const Clientpreview = () => {

    const [searchTerm, setSearchTerm] = useState("");
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const MySwal = withReactContent(Swal);
    
    // Modal states
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [clientCommands, setClientCommands] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);
    
    const getEmailLocalPart = (email) => {
        if (!email) return null;
        return email.split("@")[0] || email;
    };

    // Récupération de l'ID du restaurant et du token
    const ideresto = JSON.parse(localStorage.getItem('user'))?.restoId;

    // Récupération des clients depuis l'API
    const fetchClients = async () => {
        if (!ideresto) {
            setLoading(false);
            MySwal.fire({
                icon: "error",
                text: "Aucun restaurant sélectionné",
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 3000,
                customClass: { popup: "pixel-toast" }
            });
            return;
        }

        setLoading(true);
        try {
            const token = JSON.parse(localStorage.getItem("user"))?.access_token;
            
            const response = await axios.get(
                `${base_api_url}api/restaurants/clients/${ideresto}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            console.log("ID du restaurant:",  response.data);
            
            if (response.status === 200 || response.status === 201) {
                const clientsData = response.data;
                
                const formattedClients = clientsData.map(client => ({
                    id: client.id_client,
                    nom: client.nom_client || (client.telephone ? `Client ${client.telephone}` : getEmailLocalPart(client.email)) || "Client",
                    email: client.email || "Non renseigné",
                    telephone: client.telephone,
                    statut: client.statut,
                    fidelite: client.statut === 1,
                    date_creation: client.date_creation,
                    derniere_visite: new Date(client.date_creation).toLocaleDateString('fr-FR'),
                    initial: (client.nom_client?.charAt(0) || client.telephone?.charAt(0) || client.email?.charAt(0) || "").toUpperCase()
                }));
                
                setClients(formattedClients);
            }
        } catch (error) {
            console.error("Erreur lors de la récupération des clients:", error);
            MySwal.fire({
                icon: "error",
                text: "Impossible de charger la liste des clients.",
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 5000,
                customClass: { popup: "pixel-toast" }
            });
            setClients([]);
        } finally {
            setLoading(false);
        }
    };

    // Chargement initial des clients
    useEffect(() => {
        fetchClients();
    }, []); 

    // Calcul des statistiques
    const stats = useMemo(() => {
        const totalClients = clients.length;
        const loyalClients = clients.filter(c => c.fidelite).length;
        const now = new Date();
        const newClientsThisMonth = clients.filter(({ date_creation }) => {
            if (!date_creation) return false;
            const createdAt = new Date(date_creation);
            return createdAt.getMonth() === now.getMonth() && createdAt.getFullYear() === now.getFullYear();
        }).length;
        
        return [
            {
                label: "TOTAL CLIENTS",
                value: totalClients.toString(),
                icon: <Users size={20} />,
                colorClass: "icon-orange"
            },
            {
                label: "NOUVEAUX CE MOIS",
                value: newClientsThisMonth > 0 ? `+${newClientsThisMonth}` : newClientsThisMonth.toString(),
                icon: <UserPlus size={20} />,
                colorClass: "icon-blue"
            },
            {
                label: "CLIENTS FIDÈLES",
                value: loyalClients.toString(),
                icon: <Star size={20} />,
                 colorClass: "icon-pink"
            }
        ];
    }, [clients]);

    // Filtre des clients
    const filteredClients = useMemo(() => {
        return clients.filter(client => {
            return client.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
                   client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                   client.telephone.includes(searchTerm);
        });
    }, [clients, searchTerm]);

    // voir les commandes d'un client 
    const handleEyeClient = async (clientId) => {
        try {
            const token = JSON.parse(localStorage.getItem("user"))?.access_token;
            
            // Récupération des commandes du client
            const reponse = await axios.get(
                `${base_api_url}api/clients/commandes/${clientId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );             
            const commandes = reponse.data;
            
            // Trouver les informations du client
            const client = clients.find(c => c.id === clientId);
            
            // Ouvrir le modal avec toutes les commandes
            if (commandes && commandes.length > 0) {
                setClientCommands(commandes);
                setSelectedClient(client);
                setIsModalOpen(true);
            } else {
                MySwal.fire({
                    icon: "info",
                    text: "Aucune commande trouvée pour ce client.",
                    toast: true,
                    position: "bottom-end",
                    showConfirmButton: false,
                    timer: 3000,
                    customClass: { popup: "pixel-toast" }
                });
            }
            
        } catch (error) {
            console.error("Erreur lors de la récupération des commandes du client:", error);
            MySwal.fire({
                icon: "error",
                text: "Impossible de charger la liste des commandes du client.",
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 5000,
                customClass: { popup: "pixel-toast" }
            });
        }
    };

    // Modal handlers
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setClientCommands([]);
        setSelectedClient(null);
    };

    // Helper functions for modal
    const formatDate = (dateString) => {
        if (!dateString) return "--";
        const date = new Date(dateString);
        return date.toLocaleString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatPrice = (price) => {
        if (price === null || price === undefined) return "0.00 €";
        return `${parseFloat(price).toFixed(2)} €`;
    };


    // Affichage du chargement
    if (loading) {
        return (
            <div className="f-page">
                <div className="content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Chargement...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="f-page">
            <div className="content">

                {/* HEADER */}
                <div className="f-header">
                    <div>
                        <h1 className="f-title">Suivi Client</h1>
                        <p className="f-subtitle">
                            Gérez votre base de données client et la fidélité
                        </p>
                    </div>
                </div>

                {/* SEARCH */}
                <div className="f-search-wrap">
                    <Search className="f-search-icon" size={16} />
                    <input
                        type="text"
                        className="f-search"
                        placeholder="Rechercher un client..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                {/* STATS */}
                <div className="f-stats">
                    {stats.map((stat, index) => (
                        <div className="f-stat-card" key={index}>
                            <div>
                                <span className="f-stat-label">{stat.label}</span>
                                <div className="f-stat-value">{stat.value}</div>
                            </div>
                            <div className={`f-stat-icon ${stat.colorClass}`}>
                                {stat.icon}
                            </div>
                        </div>
                    ))}
                </div>

                {/* TABLE */}
                <div className="f-table-wrap">
                    <table className="f-table">
                        <thead>
                            <tr>
                                <th>Client</th>
                                <th>Téléphone</th>
                                <th>Dernière visite</th>
                                <th>Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {filteredClients.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="f-empty">
                                        Aucun client trouvé
                                    </td>
                                </tr>
                            ) : (
                                filteredClients.map((client) => (
                                    <tr key={client.id} className="f-row">

                                        {/* Nom */}
                                        <td>
                                            <div className="f-nom-cell">
                                                <div className="f-avatar">
                                                    {client.initial}
                                                </div>
                                                <div className="f-nom-wrap">
                                                    <span className="f-nom">{client.nom}</span>
                                                    <span className="f-contact">{client.email}</span>
                                                </div>
                                            </div>
                                        </td>

                                        {/* Contact */}
                                        <td className="f-contact">
                                            {client.telephone}
                                        </td>

                                        {/* Dernière visite */}
                                        <td>{client.derniere_visite}</td>

                                        {/* Actions */}
                                        <td className="f-actions-cell">
                                            <div className="f-row-actions">
                                                <button
                                                    className="f-action-btn f-edit"
                                                    onClick={() => handleEyeClient(client.id)}
                                                >
                                                    <Eye size={18} />
                                                </button>      
                                            </div>
                                        </td>

                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* COMMAND DETAILS MODAL */}
                {isModalOpen && selectedClient && clientCommands.length > 0 && (
                    <div className="modal-overlay" onClick={handleCloseModal}>
                        <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
                            {/* Modal Header */}
                            <div className="modal-header">
                                <div style={{ flex: 1 }}>
                                    <h2 className="modal-title">
                                        {selectedClient.telephone || selectedClient.email}
                                    </h2>
                                    <p className="modal-subtitle">
                                        {clientCommands.length} commande{clientCommands.length > 1 ? 's' : ''}
                                    </p>
                                </div>
                                <button
                                    className="modal-close-btn"
                                    onClick={handleCloseModal}
                                    aria-label="Fermer"
                                >
                                    <X size={24} />
                                </button>
                            </div>

                            {/* Modal Content */}
                            <div className="modal-content">
                                <div className="commands-list">
                                    {clientCommands.map((commande) => {
                                        return (
                                            <div key={commande.id_commande} className="command-card">
                                                {/* Command Header */}
                                                <div className="command-header">
                                                    <div className="command-info">
                                                        <h4 className="command-title">
                                                            Commande #{commande.reference || commande.id_commande}
                                                        </h4>
                                                        <div className="command-meta">
                                                            <div className="left-info">
                                                                <span className="command-price">{formatPrice(commande.prix_ttc || commande.prix)}</span>
                                                                <span className="command-phone">{commande.nom || 'N/A'}</span>
                                                            </div>
                                                        </div>
                                                    </div>            
                                                </div>

                                                {/* Command Footer */}
                                                <div className="command-footer">
                                                    <span className="command-date">{formatDate(commande.date_commande)}</span>
                                                </div>
                                               
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default Clientpreview;